import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Boxes, Search, Clock3 } from 'lucide-react';
import { apiGet } from '@/shared/api/client';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';

interface FifoItem {
  id: string;
  barcode: string;
  alias?: string | null;
  template_name?: string | null;
  sterile_expires_at: string;
  urgency: 'OK' | 'EXPIRING' | 'EXPIRED';
}

const URGENCY_TONE: Record<FifoItem['urgency'], 'success' | 'warning' | 'danger'> = {
  OK: 'success',
  EXPIRING: 'warning',
  EXPIRED: 'danger',
};

const URGENCY_LABEL: Record<FifoItem['urgency'], string> = {
  OK: 'No prazo',
  EXPIRING: 'Vence em até 3 dias',
  EXPIRED: 'Vencido',
};

function daysUntil(iso: string): number {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return NaN;
  return Math.ceil((t - Date.now()) / (1000 * 60 * 60 * 24));
}

export function ArsenalPage() {
  const [q, setQ] = useState('');
  const fifo = useQuery({
    queryKey: ['dashboard', 'fifo'],
    queryFn: () => apiGet<{ items: FifoItem[] }>('/v1/dashboard/fifo'),
    refetchInterval: 30_000,
  });

  const filtered = useMemo(() => {
    const all = fifo.data?.items ?? [];
    if (!q.trim()) return all;
    const needle = q.trim().toLowerCase();
    return all.filter((k) =>
      (k.alias ?? '').toLowerCase().includes(needle) ||
      (k.template_name ?? '').toLowerCase().includes(needle) ||
      k.barcode.toLowerCase().includes(needle),
    );
  }, [fifo.data, q]);

  const counts = useMemo(() => {
    const c = { OK: 0, EXPIRING: 0, EXPIRED: 0 };
    for (const k of fifo.data?.items ?? []) c[k.urgency]++;
    return c;
  }, [fifo.data]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
            Arsenal estéril — FIFO
          </h1>
          <p className="text-sm text-neutral-500">
            Kits liberados para uso, ordenados pela validade de esterilização (mais próximos do vencimento primeiro).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="success">No prazo: {counts.OK}</Badge>
          <Badge tone="warning">A vencer: {counts.EXPIRING}</Badge>
          <Badge tone="danger">Vencidos: {counts.EXPIRED}</Badge>
        </div>
      </header>

      <Card>
        <CardHeader
          title="Estoque por validade"
          subtitle={fifo.isLoading ? 'Carregando…' : `${filtered.length} kits exibidos (top 50 do servidor)`}
          action={
            <div className="w-64">
              <Input
                placeholder="Buscar por código, alias ou modelo"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                leadingIcon={<Search size={16} />}
              />
            </div>
          }
        />
        <CardBody className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500 dark:bg-neutral-700">
              <tr>
                <th className="px-4 py-2">Kit</th>
                <th className="px-4 py-2">Validade</th>
                <th className="px-4 py-2">Dias restantes</th>
                <th className="px-4 py-2">Situação</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((k) => {
                const d = daysUntil(k.sterile_expires_at);
                const rowTone =
                  k.urgency === 'EXPIRED' ? 'bg-danger-bg/40'
                  : k.urgency === 'EXPIRING' ? 'bg-warning-bg/40'
                  : '';
                return (
                  <tr key={k.id} className={`border-t border-neutral-200 dark:border-neutral-700 ${rowTone}`}>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Boxes size={16} className="text-neutral-400" />
                        <div className="min-w-0">
                          <div className="font-medium">{k.alias ?? k.template_name ?? '—'}</div>
                          <div className="font-mono text-xxs text-neutral-500">{k.barcode}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{k.sterile_expires_at}</td>
                    <td className="px-4 py-2 font-mono text-xs">
                      <span className="inline-flex items-center gap-1">
                        <Clock3 size={12} className="text-neutral-400" />
                        {isFinite(d) ? `${d}d` : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone={URGENCY_TONE[k.urgency]}>{URGENCY_LABEL[k.urgency]}</Badge>
                    </td>
                  </tr>
                );
              })}
              {!fifo.isLoading && filtered.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-xs text-neutral-500">Nenhum kit em arsenal.</td></tr>
              ) : null}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}
